import { useRef, useState } from 'react';
import { Animated, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { CuePill, IconButton } from '../index';

import { machineLabel } from './machineLabel';

import { SetCountRow } from './SetCountRow';
import { SupersetTabs } from './SupersetTabs';
import { RosterTitleRow } from './RosterTitleRow';
import { FocusPanelChipRow } from './FocusPanelChipRow';
import { HistoryIconButton } from './HistoryIconButton';
import { FocusPanelEffort } from './FocusPanelEffort';
import { FocusPanelInputs } from './FocusPanelInputs';
import { CalibrationCue } from './CalibrationCue';
import type { FocusPanelProps } from './FocusPanelProps';
import { makeStyles } from './FocusPanel.styles';
import { FocusPanelDetails } from './FocusPanelDetails';
import { SetTypeChipBar } from './SetTypeChipBar';
import { useAdvanceFlash } from './useAdvanceFlash';
import { useTransientTip } from './useTransientTip';
import { axisHasLoadValue, canTrainOneSide } from '@heraion/shared';
import { SideChoiceRow } from '../primitives/SideChoiceRow';
import { cardHints } from './cardHints';
import { showsPerImplementHint } from './loadingHint';
import { dropsetVariantPatch, removeDropsetPatch, toggleAssistedPatch } from './dropsetDraft';
import { isSetTimed, setNeedsDuration } from './deriveEntryKind';
import { effectivePrescriptionNote } from './prescriptionCue';

// FocusPanel (timeline logger Phase 2) — the active-exercise card + the SINGLE
// data-entry point of the timeline logger. It binds to the active exercise's
// bound set (`set`, the "next set to log") and writes through to the draft on
// every keystroke, so by LOG-SET time the values are already in the flat store
// and the shared cycleSetPill (PR check + rest timer + the bodyweight marker) sees
// fresh data.
//
// Slice 1 ("the plan IS the queue"): the old auto-appended trailing set is gone.
// Sets come from the plan (materialised up front) or the explicit ＋ on the
// SetCountRow. When every set is logged, `set` is null — the card shows the
// row + ＋ but no input, so a workout never carries a phantom trailing set
// (not-done ≠ failed; drop-uncompleted at save still drops any skipped planned
// set). The SetCountRow replaces the old "Set N" subtitle.
export function FocusPanel({
  title,
  entry,
  set,
  activeSetIdx,
  planPerSetNotes,
  planEntryNote,
  canRemove,
  exercise,
  defaultUnit,
  effortScale,
  onChange,
  onSelectSet,
  onAddSet,
  onRemoveSet,
  onInsertSetBefore,
  weekCue,
  pin,
  onToggleUnit,
  onToggleLoadedSides,
  onToggleAmrap,
  timer,
  effortMeter,
  editLocked,
  hideFail = false,
  hideBurnPlus = false,
  hideLaterality = false,
  amrapPrescribing = false,
  prescribeAxis,
  prescribeLoad,
  onToggleTimed,
  timedActive = false,
  noteActive = false,
  prescribeNoteEditor,
  prescribeEntryNoteEditor,
  onOpenPlates,
  onOpenEffortScale,
  onSwap,
  onSplitGroup,
  onOpenGroupMenu,
  onRemoveExercise,
  historyOpen,
  onToggleHistory,
  onOpenTip,
  groupMembers,
  groupLabel,
  activeEntryId,
  resolveName,
  onSelectMember,
  onCreateGroup,
  cueKey,
  flashCue,
  targetStrip,
  confirm,
  theme,
}: FocusPanelProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const p = theme.palette;

  // Advance-highlight flash (point B) — extracted to useAdvanceFlash.
  const { flashBg, flashBorder } = useAdvanceFlash(cueKey, p);

  // 🔥 Burn+ reveal — the Dropset|Myo-rep selector bar, toggled by the Burn+
  // chip in the qualifier row (the old ⋯ "Set type" menu was retired: it only
  // ever gated this one action; notes + dropset steps render on their own).
  const [burnOpen, setBurnOpen] = useState(false);

  // PE-22 K3.4 — "Nota" editor reveal, same local-UI shape as burnOpen above:
  // the chip's tap only opens/closes this card's own disclosure; the actual
  // text write goes through the injected `prescribeNoteEditor` slot's own
  // onChange, never through this boolean.
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  // The ONE close handler, reached from two affordances: the "Nota" chip
  // (re-tapping it toggles the same boolean) and the editor's own explicit
  // close button (review fix, PE-22 K3 triad, Minor a11y) — passed into the
  // `prescribeNoteEditor` render function below.
  const toggleNoteEditor = () => setNoteEditorOpen((v) => !v);

  // PE-37 — the per-EXERCISE note's reveal. A SECOND boolean on purpose, not a
  // shared one: the two notes are different facts at different levels, and one
  // toggle would make opening the exercise note close the set note the coach
  // was mid-way through writing.
  const [entryNoteOpen, setEntryNoteOpen] = useState(false);
  const toggleEntryNoteEditor = () => setEntryNoteOpen((v) => !v);

  // #308: the target rows open from row 1's target icon.
  const [targetOpen, setTargetOpen] = useState(false);

  // ONE balloon per card, under row 2, for every tip the card raises: the
  // held hints of both rows and the Burn+ bar (#308), the lock reasons, the
  // laterality explainer.
  const hints = cardHints(t);
  const help = useTransientTip(theme, {
    storageKey: 'jl.logger.holdHintIntro',
    message: hints.intro,
  });
  const hold = help.holdProps;

  const isBodyweight = entry.isBodyweight;

  // Per-implement entry tip (IB-8 family, Antenore 2026-07-14): the first
  // time per exercise the weight field gains focus on a UNI entry of a
  // handheld lift (two dumbbells / one-arm work), float the existing
  // perImplementHint balloon — «enter ONE implement's weight, the app
  // counts both». Auto-dismissing (useTransientTip), respects the global
  // Hints toggle internally, zero standing UI. Once-per-exercise via a
  // ref so re-focusing the field doesn't nag.
  const perImplementTipSeen = useRef<Set<string>>(new Set());
  const onWeightFocus = () => {
    if (!exercise || !showsPerImplementHint(exercise)) return;
    if (entry.loadedSides !== 2) return;
    if (perImplementTipSeen.current.has(entry.exerciseId)) return;
    perImplementTipSeen.current.add(entry.exerciseId);
    help.showTip(t('workout.perImplementHint'));
  };

  // Leading controls of row 2: 🕐 history, the machine tag, then the
  // superset (S+ or the N/M cue and ☰), all before BI (#308).
  const leadingIcons = (
    <>
      {/* LG-49 — the same clock, which BLINKS when this exercise has been
          left alone for a while. His instruction: the signal rides the
          control that opens the history, it is not a second element beside
          it. Plain clock when the exercise is fresh, which is most of the
          time. */}
      <HistoryIconButton
        exerciseId={entry.exerciseId}
        active={historyOpen}
        onPress={onToggleHistory}
        {...hold(hints.clock)}
        theme={theme}
        t={t}
      />
      {/* EQ-1 — which physical machine this entry is on. Shows only when the
          entry carries one, and tapping it REVEALS, never edits: changing
          machine is replace-exercise, the one place the tag is managed
          (equipment-model.md §8.4). It borrows the card's existing balloon
          rather than adding UI of its own, so a tap here still raises the
          single prompt the card is allowed. */}
      {entry.equipmentInstance ? (
        <IconButton
          name="tag"
          // `explicit`: an unpinned tip obeys the global Hints toggle, so with
          // Hints off the tap would do nothing at all. Held, it says the same.
          onPress={() =>
            help.showTip(machineLabel(entry.equipmentInstance!, t), {
              explicit: true,
            })
          }
          accessibilityLabel={machineLabel(entry.equipmentInstance, t)}
          {...hold(machineLabel(entry.equipmentInstance, t))}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
          theme={theme}
        />
      ) : null}
      <SupersetTabs
        members={groupMembers}
        activeEntryId={activeEntryId}
        groupLabel={groupLabel}
        resolveName={resolveName}
        onSelect={onSelectMember}
        onSplit={onSplitGroup ?? (() => {})}
        onCreate={onCreateGroup}
        onOpenMenu={editLocked ? undefined : onOpenGroupMenu}
        editLocked={editLocked}
        showTip={help.showTip}
        hold={hold}
        hints={hints}
        t={t}
        theme={theme}
      />
    </>
  );
  return (
    <Animated.View
      style={[styles.panel, { backgroundColor: flashBg, borderColor: flashBorder }]}
    >
      {/* The transient cue for an advance the card itself has to carry: a
          zero-rest lift, which has no countdown to hold the caption. Every
          other advance is captioned by the docked rest bar for as long as it
          runs (TM-18c). Colour alone is never the only cue. */}
      {flashCue ? <CuePill theme={theme} label={flashCue} /> : null}

      {/* #308: two rows. Row 1, the name (the picker's button) and the
          exercise actions; row 2, the clock, the superset and the chips. */}
      <RosterTitleRow
        title={title}
        onSwap={onSwap}
        onOpenTip={onOpenTip}
        target={
          targetStrip
            ? { expanded: targetOpen, onToggle: () => setTargetOpen((v) => !v) }
            : undefined
        }
        weekCue={weekCue}
        pin={pin}
        onRemoveExercise={onRemoveExercise}
        hold={hold}
        hints={hints}
        t={t}
        theme={theme}
      />

      {/* Row 2 hosts the web hover's bubble, which hangs over what follows
          instead of pushing it down under the pointer. */}
      <View style={styles.row2}>
        <FocusPanelChipRow
          entry={entry}
          set={set}
          exercise={exercise}
          onToggleLoadedSides={onToggleLoadedSides}
          onToggleAmrap={onToggleAmrap}
          onToggleAssisted={() => set && onChange(toggleAssistedPatch(set))}
          loadAxisHasValue={axisHasLoadValue(prescribeLoad)}
          dropsetActive={(set?.dropset ?? null) !== null}
          onToggleBurnPlus={() => setBurnOpen((v) => !v)}
          burnLocked={editLocked}
          showTip={help.showTip}
          hold={hold}
          hints={hints}
          leading={leadingIcons}
          hideBurnPlus={hideBurnPlus}
          hideLaterality={hideLaterality}
          amrapPrescribing={amrapPrescribing}
          onToggleTimed={onToggleTimed}
          timedActive={timedActive}
          onToggleNote={prescribeNoteEditor ? toggleNoteEditor : undefined}
          noteActive={noteActive}
          theme={theme}
        />
        {help.overlayNode}
      </View>

      {/* #75: which side the bound set is done on, seeded by the plan. Same
          reach as the UNI/BI chip: an exercise the catalog lets train one
          side, never in prescribe (the slot has its own row there). */}
      {set && !hideLaterality && !entry.isCardio && !isSetTimed(entry, set) && canTrainOneSide(exercise) ? (
        <SideChoiceRow theme={theme} side={set.side} onChange={(side) => onChange({ side })} />
      ) : null}

      {/* The card's single balloon, right under row 2: every control's held
          hint and every lock reason surface in one predictable spot. */}
      {help.tipNode}

      {/* AP-3b: the weekly target per muscle, opened by row 1's target. */}
      {targetOpen ? targetStrip : null}

      {/* K3.4 — the "Nota" editor, revealed by the chip's local open/close
          state, same placement idiom as the Burn+ bar right below it. Its own
          close button fires the SAME toggleNoteEditor the chip uses. */}
      {set && noteEditorOpen && prescribeNoteEditor ? (
        <View style={styles.setTypeBarRow}>
          {prescribeNoteEditor(toggleNoteEditor)}
        </View>
      ) : null}

      {/* 🔥 Burn+ selector — right UNDER the qualifier row that hosts the Burn+
          chip (Antenore 2026-07-02: "appena sotto la row del Burn+"), not down
          past the inputs. An active chain keeps it visible so × can undo it; a
          past-session edit can't change the type, so it stays hidden there. */}
      {set && !editLocked && (burnOpen || (set.dropset ?? null) !== null) ? (
        <View style={styles.setTypeBarRow}>
          <SetTypeChipBar
            dropset={set.dropset}
            inputsLocked={false}
            onPickVariant={(variant) => {
              const patch = dropsetVariantPatch(set, variant);
              if (patch) onChange(patch);
            }}
            onRemoveChain={() => onChange(removeDropsetPatch(set))}
            onCloseTypeBar={() => setBurnOpen(false)}
            hold={hold}
            hints={hints}
            confirm={confirm}
            theme={theme}
          />
        </View>
      ) : null}

      {/* #353: renders only inside the live logger's context. */}
      <CalibrationCue entry={entry} effortScale={effortScale} />

      {set ? (
        <FocusPanelInputs
          set={set}
          isBodyweight={isBodyweight}
          isCardio={entry.isCardio}
          isTimeBased={isSetTimed(entry, set)}
          defaultUnit={defaultUnit}
          prescribeLoad={prescribeLoad}
          onChange={onChange}
          onOpenPlates={onOpenPlates}
          onToggleUnit={onToggleUnit}
          onWeightFocus={onWeightFocus}
          theme={theme}
        />
      ) : (
        <Text style={styles.allDone}>
          {t('mobile.timeline.allLogged', {
            defaultValue: 'All sets logged. Tap + to add another.',
          })}
        </Text>
      )}

      {/* Set-count row AFTER the inputs (Antenore 2026-06-01: "la row con il
          numero di set … va dopo la riga del reps, weight e rir/rpe"). */}
      <SetCountRow
        sets={entry.sets}
        activeSetIdx={activeSetIdx}
        onSelect={onSelectSet}
        onAdd={onAddSet}
        onRemove={onRemoveSet}
        onInsertBefore={onInsertSetBefore}
        canRemove={canRemove}
        editLocked={editLocked}
        theme={theme}
      />

      <FocusPanelDetails
        set={set}
        prescriptionNote={effectivePrescriptionNote(
          activeSetIdx,
          planPerSetNotes,
        )}
        entryNote={planEntryNote || ''}
        isBodyweight={isBodyweight}
        onChangeSet={onChange}
        onEditEntryNote={
          prescribeEntryNoteEditor ? toggleEntryNoteEditor : undefined
        }
        editLocked={editLocked}
        theme={theme}
      />

      {/* PE-37 — the exercise-note editor, directly under the row that opens
          it. ⛔ Not gated on `set`, unlike the per-set editor above: an
          exercise has a note even when every set is logged and nothing is
          bound. */}
      {entryNoteOpen && prescribeEntryNoteEditor ? (
        <View style={styles.setTypeBarRow}>
          {prescribeEntryNoteEditor(toggleEntryNoteEditor)}
        </View>
      ) : null}

      {/* Effort meter (AP-0b/0c) — the completion gesture of EVERY set kind.
          One full-width easy→hard band; a tap logs the set AND records the
          effort (no escape hatch). Renders only while a set is bound. On a
          duration entry it takes the compact variant and owns the start-timer
          affordance in its slot, so there is no timer row (MX-5 slice 2). */}
      {/* K3.2 — the load-axis selector, in the card's expanded band area:
          right above the meter it decides the shape of. Undefined outside
          prescribe, so nothing renders in log/log-past. */}
      {set && prescribeAxis ? prescribeAxis : null}

      {set ? (
        <FocusPanelEffort
          set={set}
          setKey={`${entry.exerciseId}#${activeSetIdx ?? -1}`}
          effortScale={effortScale}
          value={effortMeter.value}
          failed={effortMeter.failed}
          onSelect={effortMeter.onSelect}
          onFail={effortMeter.onFail}
          flashRir={effortMeter.flashRir}
          flashKey={effortMeter.flashKey}
          onOpenEffortScale={onOpenEffortScale}
          // ...and NOT kindNeedsDuration(entry): a set the plan bounded in
          // time renders the duration variant of the meter — the clock, the
          // timer column, the wording — on any exercise. The start chip asks
          // the same predicate (timerStartHandlers), so a bout can never be
          // offered a timer the meter has nowhere to draw.
          isDuration={setNeedsDuration(entry, set)}
          isCardio={entry.isCardio}
          timer={timer}
          confirm={confirm}
          hideFail={hideFail}
          targetHold={hold(hints.effortTarget)}
          theme={theme}
        />
      ) : null}

      {/* The old exercise-controls row is gone (AP-0d): ✓/✗ became the meter's
          tap; ❓/🗑 sit on row 1 (#308). The meter is the card's last word. */}
    </Animated.View>
  );
}
